import { hasMissingReturnAddressFields } from "utils/returnAddress";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Drawer,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Skeleton,
  TextField,
} from "@mui/material";
import { PickupAddress } from "../Orders/Address";
import Header from "../common/Header";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { StyledExchange, StyledPaymentDiv, StyledReturnCart } from "./styled";
import { Controller, useForm } from "react-hook-form";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrders,
  getSelectedOrder,
  getUUID,
  getWebSelected,
  updateSelectedOrder,
  updateUUID,
} from "features/orders/orderSlice";
import CartProducts from "components/v1/common/CartProducts/CartProducts";
import {
  addPaymentDetails,
  addReturnedProducts,
  addRefundJSON,
  addRefundSource,
  getRefundJSON,
  getRefundSource,
  getReturnedProducts,
} from "features/returnedProducts/returnedProductSlice";
import { ReactComponent as CloseIcon } from "assets/close.svg";
import cartImage from "assets/empty_cart.svg";
import { PriceBox } from "components/v1/common/PriceBox";
import {
  createRequest,
  createPayment,
  paymentValidate,
  validateReturnCartAvailability,
  verifyBankDetailsApi,
} from "utils/api";
import { addReturnRequestId } from "features/successProducts/successProductsSlice";
import {
  addAddress,
  addShipmentMethod,
  getAddress,
  getShipmentMethod,
} from "features/address/addresSlice";
import {
  addUploadMediaIds,
  getPreviews,
  getUploadMediaIds,
} from "features/product/productSlice";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getEmail, getFirstName, getLastName } from "features/user/userSlice";
import ErrorModal from "components/v1/Orders/Address/ErrorModal";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { getEncipherencode } from "utils/encipherencode";
import {
  clearReturnCartContext,
  getReturnCartContext,
  saveReturnCartContext,
} from "utils/returnCartSession";
import { stripVersionPrefix, versionedPath } from "utils/routes";
import { getApiErrorMessage } from "utils/apiError";
import { hasReturnMediaUploads } from "utils/returnMedia";
import {
  createSubmissionLock,
  getCreateConflict,
  getIykaQuoteConflict,
  hasActionableAvailabilityConflicts,
} from "utils/returnRequestAvailability";
import {
  deriveRequestReturnType,
  hasExchangeFulfillmentRequestType,
  hasRefundRequestType,
  isExchangeOnlyRequestType,
} from "utils/returnTypes";
import {
  isValidEmail,
  isValidPhoneNumber,
} from "utils/contactValidation";

const BANK_VERIFICATION_FALLBACK = "Something is wrong. Please try again.";

function ReturnCart() {
  const location = useLocation();
  const currentPath = stripVersionPrefix(location.pathname);

  const [openBankForm, setOpenBankForm] = useState(false);
  const [accountNumber, setAccountNumber] = useState(null);
  const [accHolderName, setAccHolderName] = useState(null);
  const [confirmAccNumber, setConfirmAccNumber] = useState(null);
  const [hitValidateApi, setHitValidateApi] = useState("validate");
  const [ifsc, setIfsc] = useState(null);
  const [requestPaymentStatus] = useState(null);
  const refundSource = useSelector(getRefundSource);
  const merchantConfig = useSelector(getMerchantConfig);
  const rmsConfig = useSelector(getRmsConfig);
  const allOrders = useSelector(getOrders);
  const selectedOrder = useSelector(getSelectedOrder);
  const defaultShippingMethod = useSelector(getShipmentMethod);
  const addressDetails = useSelector(getAddress);
  const uploads = useSelector(getPreviews);
  const returnedProducts = useSelector(getReturnedProducts);
  const [requestCreated, setRequestCreated] = useState(false);
  const createSubmissionLockRef = useRef(null);
  const bankFormDismissedRef = useRef(false);
  if (!createSubmissionLockRef.current) {
    createSubmissionLockRef.current = createSubmissionLock();
  }
  const webSelected = useSelector(getWebSelected);
  const unique_id = useSelector(getUUID);
  const [outOfStock, setOutOfStock] = useState(null);
  // const [modalOpen, setModalOpen] = useState(false);
  // const [paymentModal, setPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [refundModeError, setRefundModeError] = useState(false);
  const [calculatingLoader, setCalculatingLoader] = useState(false);
  const [initialLoader, setInitialLoader] = useState(true);
  const [shipmentMethod, setShipmentMethod] = useState(defaultShippingMethod);
  const [requestConsentAccepted, setRequestConsentAccepted] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const [verifyingDetails, setVerifyingDetails] = useState(false);

  const missingFirstName = useSelector(getFirstName);
  const missingLastName = useSelector(getLastName);
  const missingEmail = useSelector(getEmail);
  const uploadedMediaIds = useSelector(getUploadMediaIds);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const encipherencode = getEncipherencode(params);

  const transaction_id =
    params.get("razorpay_invoice_id") || params.get("txn_id");
  const reference_id = params.get("reference_id");
  const rdv = params.get("rdv");

  const upload_media =
    hasReturnMediaUploads(uploadedMediaIds) ||
    (Array.isArray(uploads) && uploads.length > 0);

  const [updatedReturnedProducts, setUpdatedReturnedProducts] =
    useState(returnedProducts);
  const visibleShippingMethods = useMemo(
    () => [
      ...(rmsConfig.show_shipping_preference && rmsConfig.supports_return_pickup
        ? ["merchant_handles_return_shipment"]
        : []),
      ...(rmsConfig.show_shipping_preference &&
      !rmsConfig.supports_return_pickup &&
      rmsConfig.self_ship
        ? ["customer_ships_back"]
        : []),
    ],
    [
      rmsConfig.self_ship,
      rmsConfig.show_shipping_preference,
      rmsConfig.supports_return_pickup,
    ],
  );

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm();

  const submitHandler = async ({
    accountnum,
    name,
    confirmAccountnum,
    ifsc,
  }) => {
    setVerificationError("");
    const shouldVerify = rmsConfig.bank_details_verification_enabled;
    try {
      if (shouldVerify) {
        setVerifyingDetails(true);
        await verifyBankDetailsApi({
          ifsc,
          account_number: Number(accountnum),
          name,
          phone: userPhoneNumber ? Number(userPhoneNumber) : null,
          vpa: null,
        });
      }

      bankFormDismissedRef.current = true;
      setOpenBankForm(false);
      setAccountNumber(accountnum);
      setAccHolderName(name);
      setConfirmAccNumber(confirmAccountnum);
      setIfsc(ifsc);
      dispatch(
        addRefundJSON({
          bank_details: {
            bank_account_number: accountnum,
            confirm_bank_account_number: confirmAccountnum,
            bank_account_holder_name: name,
            bank_ifsc_code: ifsc,
          },
          refund_method: "bank_transfer",
        })
      );
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, BANK_VERIFICATION_FALLBACK);
      setVerificationError(errorMessage);
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } finally {
      setVerifyingDetails(false);
    }
  };

  const handleUPISubmit = async ({ upi }) => {
    setVerificationError("");
    const shouldVerify = rmsConfig.bank_details_verification_enabled;
    try {
      if (shouldVerify) {
        setVerifyingDetails(true);
        await verifyBankDetailsApi({
          ifsc: null,
          account_number: null,
          name: null,
          phone: userPhoneNumber ? Number(userPhoneNumber) : null,
          vpa: upi,
        });
      }

      setUpiValue(upi);
      setOpenBankForm(false);
      setAccountNumber(null);
      setAccHolderName(null);
      dispatch(
        addRefundJSON({
          upi: {
            upi_code: upi,
          },
          refund_method: "upi",
        })
      );
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, BANK_VERIFICATION_FALLBACK);
      setVerificationError(errorMessage);
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } finally {
      setVerifyingDetails(false);
    }
  };

  const dispatch = useDispatch();
  const syncShipmentMethod = (method) => {
    setShipmentMethod(method);
    dispatch(addShipmentMethod(method));
  };

  const return_type = deriveRequestReturnType(
    updatedReturnedProducts.map((item) => item.return_type),
    reference_id && reference_id !== null && reference_id.includes("REXC")
      ? "refund_exchange"
      : "exchange",
  );
  const refund_json = useSelector(getRefundJSON);
  const [storedContext, setStoredContext] = useState(null);
  const [isRestoringState, setIsRestoringState] = useState(true);
  const [didCheckEntryState, setDidCheckEntryState] = useState(false);
  const [upiValue, setUpiValue] = useState(
    Object.keys(refund_json).length > 0 &&
      refund_json.upi &&
      refund_json.upi.upi_code
      ? refund_json.upi.upi_code
      : "",
  );

  const hasRequiredOrderFields = (order) =>
    Boolean(
      order &&
        order.order_number &&
        order.order_id !== null &&
        order.order_id !== undefined &&
        typeof order.is_cod === "boolean" &&
        order.total_order_price !== null &&
        order.total_order_price !== undefined,
    );

  const hasAddressState = (details) =>
    Boolean(details && Object.values(details).some((value) => Boolean(value)));

  const getPreferredValue = (...values) =>
    values.find(
      (value) => value !== undefined && value !== null && value !== "",
    );

  const matchedOrder = useMemo(
    () =>
      Object.values(allOrders).find(
        (order) => order.order_number === selectedOrder?.order_number,
      ) || null,
    [allOrders, selectedOrder],
  );

  const resolvedOrder = useMemo(() => {
    if (hasRequiredOrderFields(selectedOrder)) {
      return selectedOrder;
    }

    if (hasRequiredOrderFields(matchedOrder)) {
      return matchedOrder;
    }

    if (hasRequiredOrderFields(storedContext?.selectedOrder)) {
      return storedContext.selectedOrder;
    }

    return matchedOrder || selectedOrder || storedContext?.selectedOrder || {};
  }, [matchedOrder, selectedOrder, storedContext]);

  const selectedItem =
    resolvedOrder && Object.keys(resolvedOrder).length > 0 ? [resolvedOrder] : [];

  const hasResolvedOrderContext = hasRequiredOrderFields(resolvedOrder);
  const hasReturnCart = updatedReturnedProducts.length > 0;
  const isReadyForValidation = Boolean(
    merchantConfig?.merchant_id && hasResolvedOrderContext && hasReturnCart,
  );

  const [firstNameCheck, setFirstNameCheck] = useState(
    getPreferredValue(
      addressDetails.firstName,
      missingFirstName,
      resolvedOrder?.customer_first_name,
    ) || "",
  );
  const [lastNameCheck, setLastNameCheck] = useState(
    getPreferredValue(
      addressDetails.lastName,
      missingLastName,
      resolvedOrder?.customer_last_name,
    ) || "",
  );
  const [userPhoneNumber, setUserPhoneNumber] = useState(
    getPreferredValue(
      storedContext?.customerContact?.userPhoneNumber,
      resolvedOrder?.user_phone_number,
    ) || "",
  );
  const [userEmail, setUserEmail] = useState(
    getPreferredValue(
      storedContext?.customerContact?.userEmail,
      missingEmail,
      resolvedOrder?.user_email,
    ) || "",
  );
  const hasInvalidContact =
    !isValidEmail(userEmail) || !isValidPhoneNumber(userPhoneNumber);

  let accnum = watch("accountnum");
  const format = /[!@#$%^&*)(+=<>{}[];|~`_?]/;
  const [wrongAddress, setWrongAddress] = useState(false);
  const [wrongAddressStatement, setWrongAddressStatement] = useState(
    "Please fill all the fields without any special characters (@, #, +, *, /, & , - , _)",
  );
  const [userInfoError, setUserInfoError] = useState(false);
  const [addressChange, setAddressChange] = useState(false);
  const [didCheckAddress, setDidCheckAddress] = useState(false);

  // Restore the flow once on entry; rerunning would overwrite active edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const context = getReturnCartContext();

    if (context) {
      setStoredContext(context);

      if (!hasRequiredOrderFields(selectedOrder) &&
          hasRequiredOrderFields(context.selectedOrder)) {
        dispatch(updateSelectedOrder(context.selectedOrder));
      }

      if ((!returnedProducts || returnedProducts.length === 0) &&
          Array.isArray(context.returnedProducts) &&
          context.returnedProducts.length > 0) {
        dispatch(addReturnedProducts(context.returnedProducts));
      }

      if (
        (!defaultShippingMethod || defaultShippingMethod === "") &&
        context.shipmentMethod
      ) {
        dispatch(addShipmentMethod(context.shipmentMethod));
        setShipmentMethod(context.shipmentMethod);
      }

      if (!refundSource && context.refundSource !== undefined) {
        dispatch(addRefundSource(context.refundSource));
      }

      if (
        Object.keys(refund_json || {}).length === 0 &&
        context.refundJSON &&
        Object.keys(context.refundJSON).length > 0
      ) {
        dispatch(addRefundJSON(context.refundJSON));
      }

      if (!hasAddressState(addressDetails) && hasAddressState(context.addressDetails)) {
        dispatch(addAddress(context.addressDetails));
      }

      if (
        (!uploadedMediaIds || uploadedMediaIds.length === 0) &&
        Array.isArray(context.uploadMediaIds) &&
        context.uploadMediaIds.length > 0
      ) {
        dispatch(addUploadMediaIds(context.uploadMediaIds));
      }

      if (!unique_id && context.uniqueId) {
        dispatch(updateUUID(context.uniqueId));
      }
    }

    setIsRestoringState(false);
  }, []);

  useEffect(() => {
    if (
      defaultShippingMethod &&
      defaultShippingMethod !== shipmentMethod
    ) {
      setShipmentMethod(defaultShippingMethod);
    }
  }, [defaultShippingMethod, shipmentMethod]);

  useEffect(() => {
    if (
      isRestoringState ||
      !rmsConfig.show_shipping_preference ||
      visibleShippingMethods.length === 0 ||
      visibleShippingMethods.includes(shipmentMethod)
    ) {
      return;
    }

    syncShipmentMethod(visibleShippingMethods[0]);
  }, [
    isRestoringState,
    rmsConfig.show_shipping_preference,
    shipmentMethod,
    visibleShippingMethods,
  ]);

  useEffect(() => {
    setFirstNameCheck(
      (previousValue) =>
        getPreferredValue(
          previousValue,
          storedContext?.customerContact?.firstNameCheck,
          addressDetails.firstName,
          missingFirstName,
          resolvedOrder?.customer_first_name,
        ) || "",
    );
    setLastNameCheck(
      (previousValue) =>
        getPreferredValue(
          previousValue,
          storedContext?.customerContact?.lastNameCheck,
          addressDetails.lastName,
          missingLastName,
          resolvedOrder?.customer_last_name,
        ) || "",
    );
    setUserPhoneNumber(
      (previousValue) =>
        getPreferredValue(
          previousValue,
          storedContext?.customerContact?.userPhoneNumber,
          resolvedOrder?.user_phone_number,
        ) || "",
    );
    setUserEmail(
      (previousValue) =>
        getPreferredValue(
          previousValue,
          storedContext?.customerContact?.userEmail,
          missingEmail,
          resolvedOrder?.user_email,
        ) || "",
    );
  }, [addressDetails, missingEmail, missingFirstName, missingLastName, resolvedOrder, storedContext]);

  useEffect(() => {
    if (isRestoringState || didCheckEntryState) {
      return;
    }

    const hasStoredOrderContext = hasRequiredOrderFields(storedContext?.selectedOrder);
    const hasStoredCart =
      Array.isArray(storedContext?.returnedProducts) &&
      storedContext.returnedProducts.length > 0;

    if (!hasResolvedOrderContext) {
      const message =
        "Your session expired or this page was reloaded. Please review your return selection again.";
      toast.error(message, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      clearReturnCartContext();
      setDidCheckEntryState(true);
      let url = versionedPath("/orders", location.pathname);
      if (encipherencode) {
        url += `?encipherencode=${encipherencode}`;
      }
      navigate(url);
      return;
    }

    if (!hasReturnCart && (!returnedProducts.length || !hasStoredCart)) {
      toast.error(
        "Your session expired or this page was reloaded. Please review your return selection again.",
        {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        },
      );
      clearReturnCartContext();
      setDidCheckEntryState(true);
      let url =
        hasStoredOrderContext || hasResolvedOrderContext
          ? versionedPath("/products", location.pathname)
          : versionedPath("/orders", location.pathname);
      if (encipherencode) {
        url += `?encipherencode=${encipherencode}`;
      }
      navigate(url);
      return;
    }

    setDidCheckEntryState(true);
  }, [
    didCheckEntryState,
    encipherencode,
    hasResolvedOrderContext,
    hasReturnCart,
    isRestoringState,
    navigate,
    returnedProducts.length,
    storedContext,
  ]);

  useEffect(() => {
    if (isRestoringState || !hasResolvedOrderContext) {
      return;
    }

    saveReturnCartContext({
      selectedOrder: resolvedOrder,
      returnedProducts: updatedReturnedProducts,
      shipmentMethod,
      refundSource,
      refundJSON: refund_json,
      addressDetails,
      uploadMediaIds: uploadedMediaIds,
      uniqueId: unique_id,
      customerContact: {
        firstNameCheck,
        lastNameCheck,
        userPhoneNumber,
        userEmail,
      },
    });
  }, [
    addressDetails,
    firstNameCheck,
    isRestoringState,
    lastNameCheck,
    refundSource,
    refund_json,
    resolvedOrder,
    shipmentMethod,
    unique_id,
    updatedReturnedProducts,
    uploadedMediaIds,
    userEmail,
    userPhoneNumber,
    hasResolvedOrderContext,
  ]);

  useEffect(() => {
    const totalAddressLength =
      (addressDetails.addressLine1 || "").length +
      (addressDetails.addressLine2 && addressDetails.addressLine2 !== null
        ? addressDetails.addressLine2.length
        : 0);
    if (selectedItem.length > 0) {
      if (
        hasMissingReturnAddressFields(addressDetails) ||
        format.test(addressDetails.pincode) ||
        format.test(addressDetails.addressLine1) ||
        format.test(addressDetails.addressLine2) ||
        format.test(addressDetails.state) ||
        format.test(addressDetails.city) ||
        (addressDetails.addressLine1 || "").length < 15 ||
        (addressDetails.addressLine2 &&
          addressDetails.addressLine2 !== null &&
          addressDetails.addressLine2 !== "" &&
          (addressDetails.addressLine2.length < 10 ||
            addressDetails.addressLine2.length > 70)) ||
        totalAddressLength > 150
      ) {
        setAddressChange(true);
        setWrongAddress(true);
        if (
          (addressDetails.addressLine1 || "").length < 15 ||
          (addressDetails.addressLine2 &&
            addressDetails.addressLine2 !== null &&
            addressDetails.addressLine2 !== "" &&
            addressDetails.addressLine2.length < 10)
        ) {
          setWrongAddressStatement(
            "Address is too short. Please add a valid address",
          );
        }
        if (
          (addressDetails.addressLine2 &&
            addressDetails.addressLine2 !== null &&
            addressDetails.addressLine2 !== "" &&
            addressDetails.addressLine2.length > 70) ||
          totalAddressLength > 150
        ) {
          setWrongAddressStatement(
            "Address is too long. Please add a shorter address",
          );
        }
      } else if (
        hasInvalidContact ||
        !(
          userEmail !== undefined &&
          userPhoneNumber !== undefined &&
          userEmail !== null &&
          userPhoneNumber !== null &&
          userEmail !== "" &&
          userPhoneNumber !== "" &&
          firstNameCheck &&
          lastNameCheck &&
          firstNameCheck !== null &&
          lastNameCheck !== null &&
          firstNameCheck !== "" &&
          lastNameCheck !== ""
        )
      ) {
        setAddressChange(true);
        setUserInfoError(true);
      }
      setDidCheckAddress(true);
    } else {
      setWrongAddress(false);
      setAddressChange(false);
      setUserInfoError(false);
      setDidCheckAddress(false);
    }
  }, [
    addressDetails,
    firstNameCheck,
    hasInvalidContact,
    lastNameCheck,
    format,
    resolvedOrder,
    selectedItem.length,
    userEmail,
    userPhoneNumber,
  ]);

  useEffect(() => {
    if (!didCheckAddress) {
      return;
    }

    if (addressChange) {
      setOpenBankForm(false);
      return;
    }

    if (
      refundSource === "bank_transfer" &&
      !bankFormDismissedRef.current
    ) {
      setOpenBankForm(true);
    }
  }, [addressChange, didCheckAddress, refundSource]);

  useEffect(() => {
    if (
      paymentDetails !== null &&
      paymentMethod !== "cod" &&
      paymentMethod !== "prepaid"
    ) {
      if (paymentDetails.cod && paymentDetails.prepaid) {
        if (window.screen.width < 800) {
          setPaymentMethod("prepaid");
        } else {
          setPaymentMethod("cod-prepaid");
        }
      } else if (paymentDetails.cod && !paymentDetails.prepaid) {
        setPaymentMethod("cod");
      } else if (paymentDetails.prepaid && !paymentDetails.cod) {
        setPaymentMethod("prepaid");
      } else {
        setPaymentMethod(null);
      }
    }
    if (paymentDetails !== null) {
      if (
        paymentDetails?.offer_refund_mode_on === "item_level" &&
        paymentDetails?.item_level_refund_mode_offer_amount > 0
      ) {
        if (refundSource === paymentDetails?.offer_refund_mode) {
          const refundProducts = updatedReturnedProducts.map((a) => {
            if (a.return_type === "refund") {
              return {
                ...a,
                refund_mode_offer_amount:
                  paymentDetails?.item_level_refund_mode_offer_amount,
              };
            }
            return a;
          });
          setUpdatedReturnedProducts(refundProducts);
          setHitValidateApi("dont_validate");
          setTimeout(() => {
            setHitValidateApi("validate");
          }, 200);
        }
      }
    }
  }, [paymentDetails]);

  useEffect(() => {
    if (
      requestCreated ||
      isRestoringState ||
      rdv === "no_validate" ||
      hitValidateApi !== "validate"
    ) {
      setCalculatingLoader(false);
      if (!isRestoringState) {
        setInitialLoader(false);
      }
      return;
    }

    if (!isReadyForValidation) {
      setCalculatingLoader(false);
      setInitialLoader(false);
      return;
    }

    setCalculatingLoader(true);
    setInitialLoader(true);
    paymentValidate(
      merchantConfig.merchant_id,
      resolvedOrder.order_number,
      resolvedOrder.order_id,
      resolvedOrder.is_cod,
      resolvedOrder.total_order_price,
      return_type,
      shipmentMethod,
      updatedReturnedProducts,
      refundSource,
      resolvedOrder.preapplied_exchange_charge,
      resolvedOrder.preapplied_refund_charge,
    )
      .then((resp) => {
        setPaymentDetails(resp.data);
        setCalculatingLoader(false);
        setInitialLoader(false);
        setHitValidateApi("validate");
      })
      .catch(() => {
        setCalculatingLoader(false);
        setInitialLoader(false);
      });
  }, [
    hitValidateApi,
    isReadyForValidation,
    isRestoringState,
    merchantConfig.merchant_id,
    rdv,
    refundSource,
    requestCreated,
    resolvedOrder,
    return_type,
    shipmentMethod,
    updatedReturnedProducts,
  ]);

  useEffect(() => {
    setUpdatedReturnedProducts(returnedProducts);
  }, [returnedProducts]);

  const releaseCreateLock = () => {
    createSubmissionLockRef.current.release();
    setRequestCreated(false);
  };

  const recoverFromCreateConflict = async (error) => {
    const quoteConflict = getIykaQuoteConflict(error);
    if (quoteConflict) {
      releaseCreateLock();
      setPaymentDetails(quoteConflict.paymentDetails);
      dispatch(addPaymentDetails(quoteConflict.paymentDetails));
      toast.error(quoteConflict.message, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
      });
      return true;
    }

    if (!getCreateConflict(error)) {
      return false;
    }

    releaseCreateLock();
    let freshOrder = resolvedOrder;
    let availableItems = [];

    try {
      const availability = await validateReturnCartAvailability(
        resolvedOrder,
        updatedReturnedProducts,
      );
      if (!hasActionableAvailabilityConflicts(availability)) {
        return false;
      }
      freshOrder = availability.freshOrder;
      availableItems = availability.availableItems;
      const refreshedUniqueId = availability.uniqueId || unique_id;
      dispatch(updateSelectedOrder(freshOrder));
      dispatch(updateUUID(refreshedUniqueId));
      dispatch(addReturnedProducts(availableItems));
      setUpdatedReturnedProducts(availableItems);
      saveReturnCartContext({
        ...(storedContext || {}),
        selectedOrder: freshOrder,
        returnedProducts: availableItems,
        uniqueId: refreshedUniqueId,
      });
    } catch (_) {
      return false;
    }

    toast.error(
      "This request conflicts with the latest item availability. Please review the updated order details.",
      {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      },
    );
    let url = versionedPath("/products", location.pathname);
    if (encipherencode) {
      url += `?encipherencode=${encipherencode}`;
    }
    navigate(url);
    return true;
  };

  const successCreate = (
    method = shipmentMethod,
    requestUniqueId = unique_id,
    availabilitySnapshot,
  ) => {
    const requestProducts =
      availabilitySnapshot?.availableItems || updatedReturnedProducts;
    const requestOrder = availabilitySnapshot?.freshOrder || resolvedOrder;
    setRequestCreated(true);
    createRequest(
      merchantConfig.merchant_id,
      encipherencode,
      [requestOrder],
      method,
      requestProducts,
      rmsConfig,
      addressDetails,
      upload_media,
      return_type,
      refundSource,
      refund_json && refund_json !== null ? refund_json : {},
      userEmail,
      userPhoneNumber,
      firstNameCheck,
      lastNameCheck,
      reference_id,
      uploadedMediaIds,
      paymentDetails.refund_charges + paymentDetails.preapplied_refund_charge,
      paymentDetails.exchange_charges +
        paymentDetails.preapplied_exchange_charge,
      paymentDetails.shipping_charges,
      requestUniqueId,
      refundSource === paymentDetails.offer_refund_mode
        ? paymentDetails.offer_refund_mode
        : null,
      refundSource === paymentDetails.offer_refund_mode
        ? paymentDetails.offer_refund_mode_on
        : null,
      refundSource === paymentDetails.offer_refund_mode
        ? paymentDetails.refund_mode_offer_amount
        : 0,
      rmsConfig.button_version,
      paymentDetails.discount_config,
      paymentDetails.total_exchange_charges,
      paymentDetails.total_refund_charges,
      paymentDetails.amount_to_pay,
      requestOrder.customer_id,
      paymentMethod,
      undefined,
      false,
      0,
      paymentDetails.pricing_breakdown || null,
    )
      .then((resp) => {
        dispatch(addPaymentDetails(resp.data));
        let url = versionedPath("/success", location.pathname);
        if (encipherencode) {
          url += `?encipherencode=${encipherencode}`;
        }
        navigate(url);
      })
      .catch(async (err) => {
        if (await recoverFromCreateConflict(err)) {
          return;
        }
        releaseCreateLock();
        const data = err?.response?.data;

        if (
          data?.variant_id &&
          requestProducts.some((p) => p.variant_id === data.variant_id)
        ) {
          setOutOfStock(data.variant_id);
        } else {
          toast.error(
            getApiErrorMessage(
              err,
              "Request cannot be created. Please try again"
            ),
            {
              position: toast.POSITION.TOP_RIGHT,
              autoClose: 6000,
              hideProgressBar: false,
              closeOnClick: true,
            },
          );
        }
      });
  };

  const paymentMethodValidate = (
    method = shipmentMethod,
    requestUniqueId = unique_id,
    availabilitySnapshot,
  ) => {
    const requestProducts =
      availabilitySnapshot?.availableItems || updatedReturnedProducts;
    const requestOrder = availabilitySnapshot?.freshOrder || resolvedOrder;
    if (paymentMethod == null || paymentMethod == "cod") {
      setRequestCreated(true);
      successCreate(method, requestUniqueId, availabilitySnapshot);
    } else if (paymentMethod == "cod-prepaid") {
      releaseCreateLock();
      toast.error("Please select the mode of Payment", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      if (window.screen.width < 800) {
        document
          .getElementById("paymentMode")
          .scrollIntoView({ behavior: "smooth" });
      }
      document.getElementById("paymentMode").style.color = "red";
    } else if (paymentMethod == "prepaid") {
      setRequestCreated(true);
      createRequest(
        merchantConfig.merchant_id,
        encipherencode,
        [requestOrder],
        method,
        requestProducts,
        rmsConfig,
        addressDetails,
        upload_media,
        return_type,
        refundSource,
        refund_json && refund_json !== null ? refund_json : {},
        userEmail,
        userPhoneNumber,
        firstNameCheck,
        lastNameCheck,
        reference_id,
        uploadedMediaIds,
        paymentDetails.refund_charges + paymentDetails.preapplied_refund_charge,
        paymentDetails.exchange_charges +
          paymentDetails.preapplied_exchange_charge,
        paymentDetails.shipping_charges,
        requestUniqueId,
        refundSource === paymentDetails.offer_refund_mode
          ? paymentDetails.offer_refund_mode
          : null,
        refundSource === paymentDetails.offer_refund_mode
          ? paymentDetails.offer_refund_mode_on
          : null,
        refundSource === paymentDetails.offer_refund_mode
          ? paymentDetails.refund_mode_offer_amount
          : 0,
        rmsConfig.button_version,
        paymentDetails.discount_config,
        paymentDetails.total_exchange_charges,
        paymentDetails.total_refund_charges,
        paymentDetails.amount_to_pay,
        requestOrder.customer_id,
        paymentMethod,
        undefined,
        false,
        0,
        paymentDetails.pricing_breakdown || null,
      )
        .then((resp) => {
          dispatch(addPaymentDetails(resp.data));
          let url = versionedPath("/generate-payment", location.pathname);
          if (encipherencode) {
            url += `?encipherencode=${encipherencode}`;
          }
          navigate(url);
          setPaymentMethod(null);
        })
        .catch(async (err) => {
          if (await recoverFromCreateConflict(err)) {
            return;
          }
          toast.error(
            getApiErrorMessage(err, "Something went wrong. Please try again"),
            {
              position: toast.POSITION.TOP_RIGHT,
              autoClose: 6000,
              hideProgressBar: false,
              closeOnClick: true,
            },
          );
          releaseCreateLock();
        });
    }
  };

  const createReturnRequestAfterAvailabilityCheck = (
    method = shipmentMethod,
    requestUniqueId = unique_id,
    availabilitySnapshot,
  ) => {
    setRefundModeError(false);

    if (
      hasInvalidContact ||
      wrongAddress ||
      userInfoError ||
      hasMissingReturnAddressFields(addressDetails)
    ) {
      releaseCreateLock();
      setAddressChange(true);
      setUserInfoError(true);
      return;
    }

    const showRefundError = () => {
      releaseCreateLock();
      toast.error("Please select the mode of refund.", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      setRefundModeError(true);
      if (window.screen.width < 800) {
        document
          .getElementById("Returnmode")
          ?.scrollIntoView({ behavior: "smooth" });
      }
    };

    if (method !== "") {
      const NEEDS_ACCOUNT_DETAILS = ["bank_transfer", "upi"];
      const mustValidate = rmsConfig.collect_refund_charges_on_request_creation;

      const requiresDetails = NEEDS_ACCOUNT_DETAILS.includes(refundSource);
      const hasDetails = Object.keys(refund_json).length > 0;
      const missingSource = !refundSource;
      const missingDetails = requiresDetails && !hasDetails;

      const invalid = missingSource || missingDetails; // one clear guard

      // ----- 1. Pure exchange/replacement (never touches refund flow) ------
      if (isExchangeOnlyRequestType(return_type)) {
        return paymentMethodValidate(
          method,
          requestUniqueId,
          availabilitySnapshot,
        );
      }

      // ----- 2. Any request containing refund items ------------------------
      if (hasRefundRequestType(return_type)) {
        if (invalid) return showRefundError();
        if (return_type !== "refund") {
          return paymentMethodValidate(
            method,
            requestUniqueId,
            availabilitySnapshot,
          );
        }

        return mustValidate
          ? paymentMethodValidate(
              method,
              requestUniqueId,
              availabilitySnapshot,
            )
          : (
              setRequestCreated(true),
              successCreate(method, requestUniqueId, availabilitySnapshot)
            );
      }
    } else {
      if (rmsConfig.show_shipping_preference) {
        releaseCreateLock();
        toast.error("Please select the shipping preference.", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        });
      } else {
        const fallbackMethod = "merchant_handles_return_shipment";
        setShipmentMethod(fallbackMethod);
        createReturnRequestAfterAvailabilityCheck(
          fallbackMethod,
          requestUniqueId,
          availabilitySnapshot,
        );
      }
    }
  };

  const createReturnRequest = async (method = shipmentMethod) => {
    if (
      requestCreated ||
      !createSubmissionLockRef.current.tryAcquire()
    ) {
      return;
    }

    setRequestCreated(true);

    try {
      const {
        freshOrder,
        uniqueId: validatedUniqueId,
        availableItems,
        conflicts,
      } = await validateReturnCartAvailability(
        resolvedOrder,
        updatedReturnedProducts,
      );
      const requestUniqueId = validatedUniqueId || unique_id;

      dispatch(updateSelectedOrder(freshOrder));
      dispatch(updateUUID(requestUniqueId));
      dispatch(addReturnedProducts(availableItems));
      setUpdatedReturnedProducts(availableItems);
      const reconciledContext = {
        selectedOrder: freshOrder,
        returnedProducts: availableItems,
        shipmentMethod,
        refundSource,
        refundJSON: refund_json,
        addressDetails,
        uploadMediaIds: uploadedMediaIds,
        uniqueId: requestUniqueId,
        customerContact: {
          firstNameCheck,
          lastNameCheck,
          userPhoneNumber,
          userEmail,
        },
      };
      setStoredContext(reconciledContext);
      saveReturnCartContext(reconciledContext);

      if (conflicts.length > 0) {
        releaseCreateLock();
        toast.error(
          "Your return selection changed because one or more items are no longer available. Please review the latest order details.",
          {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 6000,
            hideProgressBar: false,
            closeOnClick: true,
          },
        );
        let url = versionedPath("/products", location.pathname);
        if (encipherencode) {
          url += `?encipherencode=${encipherencode}`;
        }
        navigate(url);
        return;
      }

      createReturnRequestAfterAvailabilityCheck(
        method,
        requestUniqueId,
        {
          availableItems,
          freshOrder,
        },
      );
    } catch (error) {
      releaseCreateLock();
      toast.error(
        getApiErrorMessage(
          error,
          "We could not verify the latest item availability. Please try again.",
        ),
        {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        },
      );
    }
  };

  return (
    <StyledReturnCart sharpEdge={!merchantConfig.use_rounded_edge_buttons}>
      <Header
        ordername={resolvedOrder.order_name}
        requestPaymentStatus={requestPaymentStatus}
      />
      <div
        className="return-request"
        style={requestPaymentStatus == "pending" ? { display: "none" } : {}}
      >
        <PickupAddress
          setAddressChange={setAddressChange}
          edit={rmsConfig.enable_address_edit}
          updatedAddress={addressDetails}
          serviceable={shipmentMethod == "customer_ships_back" ? false : true}
          shipmentMethod={shipmentMethod}
          setShipmentMethod={syncShipmentMethod}
        />
        {rmsConfig.request_consent && rmsConfig.request_consent_text !== "" && (
          <label className="mobile-request-consent-label">
            <input
              type="checkbox"
              onChange={(e) => setRequestConsentAccepted(e.target.checked)}
              style={{ marginRight: "6px" }}
            />
            <span style={{ fontSize: "14px" }}>
              {rmsConfig.request_consent_text}
            </span>
          </label>
        )}
        <div style={{ display: "flex", alignItems: "center" }}>
          {paymentDetails &&
            paymentDetails !== null &&
            paymentDetails.cod &&
            paymentDetails.prepaid && (
              <FormControl className="payment-form-mob">
                <p style={{ color: merchantConfig.primary_theme_color }}>
                  PAY VIA
                </p>
                <TextField
                  style={{
                    width: "fit-content",
                    marginRight: "30px",
                  }}
                  select
                  variant="outlined"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <MenuItem value="prepaid">Prepaid</MenuItem>
                  <MenuItem value="cod">COD</MenuItem>
                </TextField>
              </FormControl>
            )}
          <Button
            type="primary"
            onClick={() => {
              createReturnRequest();
            }}
            variant="contained"
            className="request-button"
            disabled={
              !(updatedReturnedProducts.length > 0) ||
              requestCreated ||
              initialLoader ||
              calculatingLoader ||
              hasInvalidContact ||
              wrongAddress ||
              userInfoError ||
              requestPaymentStatus == "pending" ||
              (rmsConfig.request_consent && !requestConsentAccepted)
            }
            style={{
              borderRadius: merchantConfig.use_rounded_edge_buttons
                ? "10px"
                : "0px",
              fontSize: "16px"
            }}
          >
            Create request
          </Button>
        </div>
      </div>

      {requestCreated ? (
        <div
          key="request-creating-loader"
          style={{
            height: "100vh",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <div className="overlay" />
          <ClipLoader
            color="#3B444D"
            size={50}
            speedMultiplier={0.75}
            cssOverride={{ borderWidth: "6px" }}
          />
          <p
            style={{ marginTop: "10px", fontSize: "16px", textAlign: "center" }}
          >
            Loading... <br /> Please do not refresh this page
          </p>
        </div>
      ) : requestPaymentStatus == "pending" ? (
        <div
          key="payment-pending-status"
          style={{
            height: "100vh",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            color: "#3B444D",
            textAlign: "center",
            padding: "0px 15px",
          }}
        >
          <p
            style={{
              fontWeight: "700",
              fontSize: "20px",
              display: "flex",
              marginTop: "10px",
            }}
          >
            Your payment is still under processing.
          </p>
          <p style={{ marginTop: "10px" }}>
            Request will be placed once the payment is successful and you will
            be notified accordingly.
          </p>
          <p style={{ color: "#3B444D9", marginTop: "10px" }}>
            For your reference, Here is your transaction id: {transaction_id}
          </p>
          <p style={{ marginTop: "30px" }}>
            Please do not go back or refersh this page
          </p>
          <p style={{ display: "flex", alignItems: "center" }}>
            Fetching current payment status... <ClipLoader size={20} />
          </p>
        </div>
      ) : (
        <StyledExchange
          key="return-cart-content"
          returnedProducts={updatedReturnedProducts}
          webSelected={webSelected}
          location={currentPath}
          outOfStock={outOfStock}
          refundModeError={refundModeError}
          primaryColor={merchantConfig.primary_theme_color}
        >
          <div className="products-return">
            <Accordion
              defaultExpanded={
                window.screen.width > 800 || rmsConfig.only_replacement
              }
              className="accordion"
              style={{
                boxShadow: "none",
                borderRadius: merchantConfig.use_rounded_edge_buttons
                  ? "10px"
                  : "0px",
              }}
              sx={{
                borderRadius: merchantConfig.use_rounded_edge_buttons
                  ? "10px"
                  : "0px",
                "&:before": { display: "none" }, // removes default divider line
                overflow: "hidden", // ensures rounded edges are clipped
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    style={{ color: merchantConfig.primary_theme_color }}
                  />
                }
                aria-controls="panel1-content"
                style={{
                  background:
                    merchantConfig.merchant_id === 977
                      ? "#DFE6F7"
                      : `${merchantConfig.primary_theme_color}15`,
                  color: merchantConfig.primary_theme_color,
                }}
              >
                <p style={{ fontWeight: "700", letterSpacing: "1px" }}>
                  {returnedProducts.length > 0 ? returnedProducts.length : "No"}{" "}
                  item
                  {(returnedProducts.length > 1 ||
                    returnedProducts.length === 0) &&
                    "s"}{" "}
                  in the return cart
                </p>
              </AccordionSummary>
              <AccordionDetails>
                <Grid
                  alignItems="center"
                  container
                  gridTemplateColumns="repeat(12, 1fr)"
                  className="cart-products"
                >
                  {updatedReturnedProducts.length > 0 ? (
                    updatedReturnedProducts.map((product, i) => {
                      return (
                        <Grid key={i} justifyContent="center" item xs={12}>
                          <CartProducts
                            cart_type="yet_to_create"
                            product={product}
                            location={currentPath}
                            returnedProducts={updatedReturnedProducts}
                            outOfStock={outOfStock}
                          />
                        </Grid>
                      );
                    })
                  ) : (
                    <Grid
                      item
                      xs={12}
                      style={{
                        background: `#fff`,
                        padding: "20px 0px",
                        minHeight: "200px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "600",
                        flexDirection: "column",
                      }}
                    >
                      <LazyLoadImage
                        effect="opacity"
                        src={cartImage}
                        alt=""
                        style={{ height: "100px" }}
                      />
                      <span style={{ marginTop: "1.5rem" }}>
                        🚚 Ready to Return? Your Cart is Waiting!
                      </span>
                      <Button
                        variant="contained"
                        style={{ margin: "20px 0px" }}
                        onClick={() => {
                          let url = versionedPath("/products", location.pathname);
                          if (encipherencode) {
                            url += `?encipherencode=${encipherencode}`;
                          }
                          navigate(url);
                        }}
                      >
                        Add Products
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </div>

          {/* <div
              style={{
                borderRight: "2px dashed rgba(17, 17, 17, 0.2)",
                flex: "0.05",
              }}
            /> */}

          <div className="price-details">
            {addressChange && (
              <ErrorModal
                setWrongAddressStatement={setWrongAddressStatement}
                wrongAddressStatement={wrongAddressStatement}
                wrongAddress={wrongAddress}
                setWrongAddress={setWrongAddress}
                userInfoError={userInfoError}
                setUserInfoError={setUserInfoError}
                selectedItem={selectedItem}
                format={format}
                firstName={firstNameCheck}
                setFirstName={setFirstNameCheck}
                lastName={lastNameCheck}
                setLastName={setLastNameCheck}
                userPhoneNumber={userPhoneNumber}
                setUserPhoneNumber={setUserPhoneNumber}
                userEmail={userEmail}
                setUserEmail={setUserEmail}
                setAddressChange={setAddressChange}
                addressDetails={addressDetails}
              />
            )}

            <div className="pickup-address-web">
              <PickupAddress
                setAddressChange={setAddressChange}
                edit={rmsConfig.enable_address_edit}
                updatedAddress={addressDetails}
                serviceable={
                  shipmentMethod == "customer_ships_back" ? false : true
                }
                shipmentMethod={shipmentMethod}
                setShipmentMethod={syncShipmentMethod}
              />
              <hr style={{ margin: "1rem 0px" }} />
            </div>

            {initialLoader ? (
              <div>
                <Skeleton style={{ width: "100%" }} height={50} />
                <Skeleton style={{ width: "100%" }} height={50} />
                <Skeleton style={{ width: "100%" }} height={50} />
              </div>
            ) : (
              <div>
                {updatedReturnedProducts.length > 0 &&
                  paymentDetails !== null &&
                  hasRefundRequestType(return_type) && (
                    <PriceBox
                      infoType="Return"
                      setOpenBankForm={setOpenBankForm}
                      refundSource={refundSource}
                      accountNumber={accountNumber}
                      confirmAccNumber={confirmAccNumber}
                      accHolderName={accHolderName}
                      ifsc={ifsc}
                      upiValue={upiValue}
                      returnType={return_type}
                      returnedProducts={updatedReturnedProducts}
                      paymentDetails={paymentDetails}
                      setPaymentDetails={setPaymentDetails}
                      calculatingLoader={calculatingLoader}
                      setCalculatingLoader={setCalculatingLoader}
                    />
                  )}

                {paymentDetails &&
                  paymentDetails !== null &&
                  return_type !== "refund" &&
                  !(
                    (paymentDetails.reimbursement === 0 ||
                      paymentDetails.amount_to_pay === 0) &&
                    rmsConfig.only_replacement
                  ) && (
                    <StyledPaymentDiv>
                      <p className="payment-title">
                        {rmsConfig.only_replacement ? "Replace" : "Exchange"}{" "}
                        Payment Details
                      </p>
                      <div className="subtotal-statement">
                        <p>
                          Subtotal of Old Products{" "}
                          {rmsConfig.only_replacement
                            ? "(Replace)"
                            : "(Exchange)"}
                        </p>
                        <p
                          style={{
                            color: "#3B444D",
                            fontWeight: "400",
                            whiteSpace: "nowrap",
                            marginLeft: "1rem",
                          }}
                        >
                          ₹ {paymentDetails.old_item_total}
                        </p>
                      </div>

                      <div className="subtotal-statement">
                        <p>
                          Subtotal of New Products{" "}
                          {rmsConfig.only_replacement
                            ? "(Replace)"
                            : "(Exchange)"}
                        </p>
                        <p
                          style={{
                            color: "#3B444D",
                            fontWeight: "400",
                            marginLeft: "1rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          ₹ {paymentDetails.new_item_total}
                        </p>
                      </div>

                      {paymentDetails.exchange_charges +
                        paymentDetails.preapplied_exchange_charge >
                        0 && (
                        <div className="subtotal-statement">
                          <p style={{ color: "#3B444D" }}>
                            {paymentDetails.exchange_charge_label &&
                            paymentDetails.exchange_charge_label !== ""
                              ? paymentDetails.exchange_charge_label
                              : "Handling Charges"}
                          </p>
                          <p
                            style={{
                              color: "#e74c3c",
                              fontWeight: "400",
                              marginLeft: "1rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            ₹{" "}
                            {paymentDetails.exchange_charges +
                              paymentDetails.preapplied_exchange_charge}
                          </p>
                        </div>
                      )}

                      {hasExchangeFulfillmentRequestType(return_type) &&
                        paymentDetails.shipping_charges > 0 && (
                          <div className="subtotal-statement">
                            <p style={{ color: "#3B444D" }}>
                              {paymentDetails.shipping_charge_label &&
                              paymentDetails.shipping_charge_label !== ""
                                ? paymentDetails.shipping_charge_label
                                : "Shipping Charges"}
                            </p>
                            <p
                              style={{
                                color: "#e74c3c",
                                fontWeight: "400",
                                marginLeft: "1rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              ₹ {paymentDetails.shipping_charges}
                            </p>
                          </div>
                        )}

                      {paymentDetails.discount_applied &&
                        paymentDetails?.discount_config?.target_type !==
                          "line_item" && (
                          <div className="subtotal-statement">
                            <p style={{ color: "#16AC37" }}>
                              Additional Discount ({" "}
                              {paymentDetails.discount_config.title} )
                            </p>
                            <p
                              style={{
                                color: "#16AC37",
                                fontWeight: "400",
                                marginLeft: "1rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              - ₹ {paymentDetails.total_discount_value}
                            </p>
                          </div>
                        )}

                      <div
                        style={{
                          borderTop: "1px dashed #3B444DB2",
                          padding: "0.5rem 0px",
                          margin: "1rem 0",
                        }}
                        className="subtotal-statement"
                      >
                        {paymentDetails.amount_to_pay >= 0 ? (
                          <Fragment>
                            <p
                              style={{
                                color: merchantConfig.primary_theme_color,
                                fontWeight: "700",
                              }}
                            >
                              Total Payable Amount
                            </p>
                            <p
                              style={{
                                fontWeight: "700",
                                marginLeft: "1rem",
                                whiteSpace: "nowrap",
                                color: merchantConfig.primary_theme_color,
                              }}
                            >
                              {calculatingLoader
                                ? "Calculating..."
                                : `₹ ${paymentDetails.amount_to_pay}`}
                            </p>
                          </Fragment>
                        ) : paymentDetails.reimbursement > 0 ? (
                          <Fragment>
                            <p style={{ color: "#3B444D" }}>You will get</p>
                            <p
                              style={{
                                color: "#16AC37",
                                fontWeight: "700",
                                marginLeft: "1rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              ₹ {paymentDetails.reimbursement}
                            </p>
                          </Fragment>
                        ) : (
                          ""
                        )}
                      </div>
                      {(paymentDetails.reimbursement > 0 ||
                        paymentDetails.amount_to_pay < 0) && (
                        <p
                          style={{
                            color: "#3B444D",
                            fontSize: "12px",
                          }}
                          className="payment-reimbursement"
                        >
                          You will be reimbursed an amount of{" "}
                          <span style={{ color: "#16AC37", fontWeight: "600" }}>
                            ₹ {Number(paymentDetails.reimbursement)}
                          </span>
                          * (The refund amount shown is the maximum you may
                          receive. The final amount will be determined at the
                          time of reimbursement)
                        </p>
                      )}
                    </StyledPaymentDiv>
                  )}
              </div>
            )}
            {paymentDetails &&
              paymentDetails !== null &&
              paymentDetails.cod &&
              paymentDetails.prepaid && (
                <FormControl className="payment-form-web">
                  <FormLabel
                    id="paymentMode"
                    style={{
                      color: "#3B444D",
                      marginBottom: "0px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Select payment Mode:
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="paymentMode"
                    name="paymentMode"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                    }}
                    className="payment-radio-buttons"
                  >
                    <FormControlLabel
                      value="prepaid"
                      checked={paymentMethod == "prepaid"}
                      control={<Radio size="small" />}
                      label={
                        <span style={{ marginRight: "10px" }}>Prepaid</span>
                      }
                      onChange={() => {
                        setPaymentMethod("prepaid");
                        document.getElementById("paymentMode").style.color =
                          "#3B444D";
                      }}
                    />
                    <FormControlLabel
                      value="cod"
                      checked={paymentMethod == "cod"}
                      control={<Radio size="small" />}
                      label={<span>COD</span>}
                      onChange={() => {
                        setPaymentMethod("cod");
                        document.getElementById("paymentMode").style.color =
                          "#3B444D";
                      }}
                      style={{
                        marginRight: "0",
                      }}
                    />
                  </RadioGroup>
                </FormControl>
              )}
            {rmsConfig.request_consent &&
              rmsConfig.request_consent_text !== "" && (
                <label className="desktop-request-consent-label">
                  <input
                    type="checkbox"
                    onChange={(e) =>
                      setRequestConsentAccepted(e.target.checked)
                    }
                    style={{ marginRight: "6px" }}
                  />
                  <span style={{ fontSize: "14px" }}>
                    {rmsConfig.request_consent_text}
                  </span>
                </label>
              )}
            <Button
              type="primary"
              onClick={() => {
                createReturnRequest();
              }}
              variant="contained"
              className="request-button"
              disabled={
                !(updatedReturnedProducts.length > 0) ||
                requestCreated ||
                initialLoader ||
                calculatingLoader ||
                hasInvalidContact ||
                wrongAddress ||
                userInfoError ||
                requestPaymentStatus == "pending" ||
                (rmsConfig.request_consent && !requestConsentAccepted)
              }
              style={{
                borderRadius: merchantConfig.use_rounded_edge_buttons
                  ? "10px"
                  : "0px",
                fontSize: "16px"
              }}
            >
              Create request
            </Button>

            {/* {merchantConfig.whatsapp_help &&
                merchantConfig.whatsapp_help !== null && (
                  <a
                    href={merchantConfig.whatsapp_help}
                    target="_blank"
                    rel="noreferrer"
                    className="help"
                    style={{ display: "block" }}
                  >
                    Need Help?
                  </a>
                )} */}
          </div>
          {/* Bank Form */}

          <Drawer
            classes={{ paper: "paper-drawer" }}
            anchor="bottom"
            open={openBankForm}
            onClose={() => {
              bankFormDismissedRef.current = true;
              setOpenBankForm(false);
              setVerificationError("");
              if (
                upiValue === null ||
                upiValue === "" ||
                accnum === "" ||
                accnum === null
              ) {
                dispatch(addRefundSource(null));
              }
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.08)",
                  height: "80px",
                  padding: "32px",
                }}
              >
                  <p
                    style={{
                      fontSize: "1rem",
                      fontWeight: "500",
                      color: "#3B444D",
                    }}
                  >
                    {refundSource === "upi"
                      ? "Enter UPI details"
                      : "Enter Bank Details"}
                  </p>
                  <p
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      bankFormDismissedRef.current = true;
                      setOpenBankForm(false);
                      setVerificationError("");
                      if (
                        upiValue === null ||
                        upiValue === "" ||
                        accnum === "" ||
                        accnum === null
                      ) {
                        dispatch(addRefundSource(null));
                      }
                    }}
                  >
                    {" "}
                    <CloseIcon
                      style={{ width: "24px", height: "24px", stroke: "#333" }}
                    />{" "}
                  </p>
                </div>

                {refundSource === "bank_transfer" ? (
                  <form onSubmit={handleSubmit(submitHandler)}>
                    <div style={{ padding: "20px" }}>
                      <Controller
                        control={control}
                        defaultValue=""
                        rules={{ required: true, pattern: /^\d{9,18}$/ }}
                        name="accountnum"
                        render={({ field }) => (
                          <TextField
                            variant="outlined"
                            autoFocus
                            id="accountnum"
                            error={Boolean(errors.accountnum)}
                            helperText={
                              errors.accountnum
                                ? errors.accountnum.type === "pattern"
                                  ? "Account number must be more than 9 numbers"
                                  : errors.accountnum.type === "required"
                                  ? "Account Number is required"
                                  : ""
                                : ""
                            }
                            label="Account Number"
                            defaultValue=""
                            fullWidth
                            inputProps={{ type: "text" }}
                            style={{ marginTop: "20px" }}
                            {...field}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        defaultValue=""
                        rules={{
                          required: true,
                          pattern: /^\d{9,18}$/,
                          validate: (value) =>
                            value === accnum || "Account numbers do not match",
                        }}
                        name="confirmAccountnum"
                        render={({ field }) => (
                          <TextField
                            variant="outlined"
                            id="confirmAccountnum"
                            error={Boolean(errors.confirmAccountnum)}
                            helperText={
                              errors.confirmAccountnum
                                ? errors.confirmAccountnum.type === "pattern"
                                  ? "Account number must be more than 9 numbers"
                                  : errors.confirmAccountnum.type === "required"
                                  ? "Account Number is required"
                                  : errors.confirmAccountnum.type === "validate"
                                  ? "Account numbers do not match"
                                  : ""
                                : ""
                            }
                            label="Re-enter Account Number"
                            defaultValue=""
                            fullWidth
                            inputProps={{ type: "text" }}
                            style={{ marginTop: "20px" }}
                            {...field}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        defaultValue=""
                        rules={{ required: true, minLength: 2 }}
                        name="name"
                        render={({ field }) => (
                          <TextField
                            variant="outlined"
                            fullWidth
                            id="name"
                            label="Account Holder Name"
                            error={Boolean(errors.name)}
                            helperText={
                              errors.name
                                ? errors.name.type === "minLength"
                                  ? "Name length must be more than 2"
                                  : "Name is required"
                                : ""
                            }
                            inputProps={{ type: "text" }}
                            style={{ marginTop: "20px" }}
                            {...field}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        defaultValue=""
                        rules={{
                          required: true,
                          pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/,
                        }}
                        name="ifsc"
                        render={({ field }) => (
                          <TextField
                            variant="outlined"
                            fullWidth
                            id="ifsc"
                            label="IFSC"
                            error={Boolean(errors.ifsc)}
                            helperText={
                              errors.ifsc
                                ? errors.ifsc.type === "pattern"
                                  ? "IFSC code doesn't match required pattern"
                                  : "IFSC code is required"
                                : ""
                            }
                            inputProps={{ type: "text" }}
                            style={{ marginTop: "20px" }}
                            {...field}
                          />
                        )}
                      />
                      {verificationError && (
                        <p style={{ color: "#D32F2F", marginTop: "12px" }}>
                          {verificationError}
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      disabled={verifyingDetails}
                      style={{
                        padding: "1rem",
                        borderRadius: !merchantConfig.use_rounded_edge_buttons
                          ? "0px"
                          : "4px",
                        margin: "40px auto",
                        display: "block",
                        cursor: "pointer",
                        textTransform: "capitalize",
                      }}
                    >
                      {verifyingDetails ? "Verifying..." : "Save & Continue"}
                    </Button>
                  </form>
                ) : refundSource === "upi" ? (
                <form
                  onSubmit={handleSubmit(handleUPISubmit)}
                  style={{ padding: "20px" }}
                >
                  <div>
                    <Controller
                      control={control}
                      rules={{
                        required: true,
                        validate: (value) =>
                          !/@paytm$/i.test(value) ||
                          "Paytm UPI IDs are not supported",
                        pattern: {
                          value: /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/,
                          message: "Invalid UPI ID",
                        },
                      }}
                      name="upi"
                      render={({ field }) => (
                        <TextField
                          variant="outlined"
                          fullWidth
                          id="upi"
                          label="UPI ID"
                          error={Boolean(errors.upi)}
                          helperText={
                            errors.upi
                              ? errors.upi.type === "validate"
                                ? "Paytm UPI IDs are not supported"
                                : errors.upi.type === "pattern"
                                  ? "Invalid UPI ID"
                                  : "UPI code is required"
                              : ""
                          }
                          inputProps={{ type: "text" }}
                          style={{ marginTop: "20px" }}
                          {...field}
                        />
                      )}
                    />
                    {verificationError && (
                      <p style={{ color: "#D32F2F", marginTop: "12px" }}>
                        {verificationError}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={verifyingDetails}
                    style={{
                      padding: "1rem",
                      borderRadius: !merchantConfig.use_rounded_edge_buttons
                        ? "0px"
                        : "4px",
                      margin: "40px auto",
                      display: "block",
                      cursor: "pointer",
                      textTransform: "capitalize",
                    }}
                  >
                    {verifyingDetails ? "Verifying..." : "Save & Continue"}
                  </Button>
                </form>
                ) : (
                  ""
                )}
              </div>
            </Drawer>
          </StyledExchange>
      )}
    </StyledReturnCart>
  );
}

export default ReturnCart;
